import { Grid, MenuItem, SelectChangeEvent, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { useListController, useRecordContext } from "ra-core";
import { FC, useEffect, useMemo, useState } from "react";
import { useHasPermit } from "@/authProvider/useHasPermit";
import SelectField from "@/components/formik/SelectField";
import { DataTypeMaskingKind, decideDataTypeMaskingKind, dataTypeDisplayInfo } from "@/components/governance/fieldEdit/maskingRuleDataTypesMap";
import { MANAGE_OBFUSCATION_RULES } from "@/constants";
import { RecPatch } from "@/dataProvider/tools";
import { DatabaseOptionResponseDto, DatabaseType, FieldResponseDto, ObfuscationDataType, ObfuscationRuleDto, ObfuscationRuleStatus, OptionResponseDto } from "@/gen/api/encrypt";
import { extractFromField, Inferred } from "./obfsRuleCompatibleMapping";


const idFn = (x:any) => x
const sortByName = (x:any[]) => x.sort((a, b) => a.name < b.name ? -1 : 1)
const sortByText = (x:any[]) => x.sort((a, b) => a.text < b.text ? -1 : 1)

const useListItems = ({resource, filter}: { resource: string, filter: any }, tranform?: (x: any[]) => any[]) => {
  const { data } = useListController({ 
    resource: filter ? resource : 'noop', 
    basePath: '', 
    filter: filter || {}, 
    options: { action: 'OBFS_RULE_EDIT' },
  })

  const tranform1 = tranform || idFn
  return useMemo(() => tranform1(Object.values(data || {})), [data, tranform1])
}

const typeKindMap: Record<ObfuscationDataType, DataTypeMaskingKind> = {
  [ObfuscationDataType.String]:   'string',
  [ObfuscationDataType.DateTime]: 'date',
  [ObfuscationDataType.Integer]:  'integer_num',
  [ObfuscationDataType.Numeric]:  'non_integer_num',
}

const TableColumnRuleInputs: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_OBFUSCATION_RULES])
  const record = useRecordContext<ObfuscationRuleDto&RecPatch>()
  const { values, setValues } = useFormikContext<{
    dataType: ObfuscationDataType,
    databaseId: number|'',
    tableId: number|'',
    fieldId: number|'',
    inferredType: Inferred|null,
  }>()
  
  const dbs = useListItems({ resource: 'settings-select-databases', filter: {} }, sortByText) as DatabaseOptionResponseDto[]
  const tables = useListItems({ resource: 'settings-select-tables', filter: values.databaseId 
                              && { databaseId: values.databaseId } }, sortByText) as OptionResponseDto[]
  
  const currDbType = dbs.find(db => db.key === values.databaseId)?.databaseType
  const fieldsByDataType = useMemo(() => {
    const allowedRuleTypeKinds = 
      record 
      ? [typeKindMap[(record.dataType || ObfuscationDataType.String)]]
      : ['string', 'integer_num', 'non_integer_num', 'date']
    return (arr:FieldResponseDto[]) =>
      sortByName(arr.filter(x => {
        const t = x.type!.toLowerCase()
        return (
          allowedRuleTypeKinds.indexOf(decideDataTypeMaskingKind(t)) > -1 &&
          (currDbType === DatabaseType.Snowflake || ['ntext', 'text'].indexOf(t) === -1) &&
          (currDbType !== DatabaseType.DB2 || (['clob', 'dbclob'].indexOf(t)) === -1)
        )
      }))
  }, [record, currDbType])

  const fields = useListItems({ resource: 'settings-select-fields', filter: (values.databaseId && values.tableId) 
                              && { databaseId: values.databaseId, tableId: values.tableId } }, fieldsByDataType) as FieldResponseDto[]
                              
  const [selectedFieldInfo, setSelectedFieldInfo] = useState<FieldResponseDto|null>(null)
  
  const handleFieldChange = (e: SelectChangeEvent<number>) => {
    const value = e.target.value
    if (typeof(value) === 'string') { return }
    const field = fields.find(f => f.fieldId === value)
    if (!field) { return }
    setSelectedFieldInfo(field)
    setValues(prevVals => {
      return {
        ...prevVals,
        fieldId: value || 0,
        ...extractFromField(field),
      }
    })
  }

  useEffect(() => {
    if (!values.fieldId && values.fieldId !== 0) {
      setSelectedFieldInfo(null)      
    } else {
      const field = fields.find(f => f.fieldId === values.fieldId && f.tableId === +values.tableId && f.databaseId === +values.databaseId)
      setSelectedFieldInfo(field || null)
      if (!values.inferredType && field) {
        setValues(v => ({
          ...v,
          ...extractFromField(field),
        }))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.fieldId, fields, setValues, !!values.inferredType])
  
  return (
    <>
      <Grid item xs={12} >
        {record?.status === ObfuscationRuleStatus.Obsolete && 
          <Typography variant="caption" color="warning.main" sx={{ mb: 2, display: 'inline-block' }}>
            The rule is obsolete due to database schema change. Update the rule to re-enable again.
          </Typography>}
        <SelectField 
          disabled={!hasWritePermit}
          label="Database*" 
          name="databaseId" 
          onChange={(e) => {
            setValues(prevVals => ({
              ...prevVals,
              databaseId: (e.target.value as number) || '',
              tableId: '',
              fieldId: '',
              inferredType: null,
            }))
          }}
        >
          {dbs.map(db => (
            <MenuItem key={db.key} value={(db.key || '') as any} >{db.text}</MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid item xs={12} >
        <SelectField 
          disabled={!hasWritePermit || !values.databaseId}
          label="Table*" 
          name="tableId" 
          onChange={(e) => {
            setValues(prevVals => ({
              ...prevVals,
              tableId: (e.target.value as number) || '',
              fieldId: '',
              inferredType: null,
            }))
          }}
        >
          {tables.map(table => (
            <MenuItem key={table.key} value={(table.key || '') as any} >
              {
                table.text && 
                <>
                  {table.text.split('.').map((s, i) => <Typography key={i} variant={i===0 ? 'caption' : 'body2'}>{i > 0 && '. '}{s}</Typography>)}                
                </>
              }
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid item xs={12} >
        <SelectField 
          disabled={!hasWritePermit || !values.databaseId || !values.tableId}
          label="Field*" 
          name="fieldId" 
          onChange={handleFieldChange as any}
        >
          {fields.map(field => (
            <MenuItem key={field.fieldId} value={(field.fieldId || '') as any} >{field.name}</MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid item xs={12} sx={{ visibility: selectedFieldInfo ? 'visible' : 'hidden' }}>
        <Typography variant="body2" sx={{ ml: 2 }}>
          Data Type: <b>{selectedFieldInfo && dataTypeDisplay(selectedFieldInfo)}</b>
        </Typography>
      </Grid>
    </>
  )
}

export default TableColumnRuleInputs;

const dataTypeDisplay = (d:FieldResponseDto) => {
  const info = dataTypeDisplayInfo({...d, dataType: d.type})
  return `${info.dataType}${info.dataTypeExt || ''}`
}